import { z } from 'zod';
/**
 * Importação para a clínica: planilha (CSV ou XLSX) ou os contatos do telefone.
 * O arquivo vira um rascunho com as colunas reconhecidas; a pessoa confirma o
 * mapeamento, vê a prévia com o que não passou e importa. O que não passou fica
 * na quarentena com o motivo, nunca some; a importação inteira pode ser desfeita
 * por 7 dias. (PLANO.md §6.)
 */
export const CLINIC_IMPORT_KINDS = ['pacientes', 'agenda', 'financeiro', 'completo'];
export const clinicImportKindSchema = z.enum(CLINIC_IMPORT_KINDS);
export const CLINIC_IMPORT_KIND_LABEL = {
    pacientes: 'Pacientes',
    agenda: 'Agenda',
    financeiro: 'Financeiro',
    completo: 'Planilha completa',
};
/**
 * Os tipos que os apps já instalados conhecem. A "Planilha completa" (fase D) só aparece
 * na lista para quem pede (`?todas=1`): o leitor dos apps antigos tem os três tipos fixos.
 */
export const CLINIC_IMPORT_LEGACY_KINDS = [
    'pacientes',
    'agenda',
    'financeiro',
];
export const CLINIC_IMPORT_SOURCES = ['csv', 'xlsx', 'contatos'];
export const clinicImportSourceSchema = z.enum(CLINIC_IMPORT_SOURCES);
export const CLINIC_IMPORT_MAX_BYTES = 8 * 1024 * 1024;
export const CLINIC_IMPORT_MAX_ROWS = 5000;
export const CLINIC_IMPORT_PREVIEW_ROWS = 20;
export const CLINIC_IMPORT_UNDO_DAYS = 7;
/** Campos que a clínica pode ter na planilha; cada um com os nomes de coluna que reconhecemos. */
export const CLINIC_IMPORT_FIELDS = [
    'nome',
    'telefone',
    'cpf',
    'email',
    'nascimento',
    'sexo',
    'origem',
    'status',
    'observacoes',
    'tratamento',
    'tratamento_inicio',
    'tratamento_meses',
    'tratamento_preco',
    'tratamento_status',
    'consulta_data',
    'consulta_hora',
    'consulta_tipo',
    'consulta_profissional',
    'consulta_status',
    'pagamento_descricao',
    'pagamento_valor',
    'pagamento_vencimento',
    'pagamento_pago_em',
    'pagamento_forma',
];
export const clinicImportFieldSchema = z.enum(CLINIC_IMPORT_FIELDS);
export const CLINIC_IMPORT_FIELD_INFO = {
    nome: {
        label: 'Nome',
        kinds: ['pacientes', 'agenda', 'financeiro'],
        required: true,
        synonyms: [
            'nome',
            'paciente',
            'nome completo',
            'cliente',
            'lead',
            'nome do paciente',
            'name',
            'contato',
        ],
    },
    telefone: {
        label: 'Telefone',
        kinds: ['pacientes', 'agenda', 'financeiro'],
        synonyms: [
            'telefone',
            'celular',
            'whatsapp',
            'fone',
            'tel',
            'phone',
            'telefone celular',
            'contato telefone',
            'mobile',
        ],
    },
    cpf: {
        label: 'CPF',
        kinds: ['pacientes', 'agenda', 'financeiro'],
        synonyms: ['cpf', 'documento', 'cpf/cnpj'],
    },
    email: { label: 'E-mail', kinds: ['pacientes'], synonyms: ['email', 'e-mail', 'mail'] },
    nascimento: {
        label: 'Nascimento',
        kinds: ['pacientes'],
        synonyms: ['nascimento', 'data de nascimento', 'dt nascimento', 'aniversario', 'birth', 'nasc'],
    },
    sexo: { label: 'Sexo', kinds: ['pacientes'], synonyms: ['sexo', 'genero', 'gênero'] },
    origem: {
        label: 'Origem',
        kinds: ['pacientes'],
        synonyms: ['origem', 'como conheceu', 'canal', 'fonte', 'indicacao', 'source'],
    },
    status: {
        label: 'Status',
        kinds: ['pacientes'],
        synonyms: ['status', 'situacao', 'situação', 'etapa', 'status do paciente'],
    },
    observacoes: {
        label: 'Observações',
        kinds: ['pacientes'],
        synonyms: ['observacoes', 'observações', 'obs', 'notas', 'anotacoes', 'comentarios'],
    },
    tratamento: {
        label: 'Tratamento',
        kinds: ['pacientes'],
        synonyms: [
            'tratamento',
            'tipo de tratamento',
            'programa',
            'procedimento',
            'plano',
            'treatment',
        ],
    },
    tratamento_inicio: {
        label: 'Início do tratamento',
        kinds: ['pacientes'],
        synonyms: [
            'inicio',
            'início',
            'data de inicio',
            'tratamento inicio',
            'inicio do tratamento',
            'start',
        ],
    },
    tratamento_meses: {
        label: 'Meses',
        kinds: ['pacientes'],
        synonyms: ['meses', 'duracao', 'duração', 'duracao meses', 'tratamento meses'],
    },
    tratamento_preco: {
        label: 'Preço do tratamento',
        kinds: ['pacientes'],
        synonyms: ['preco', 'preço', 'valor do tratamento', 'preco tto', 'valor', 'tratamento preco'],
    },
    tratamento_status: {
        label: 'Status do tratamento',
        kinds: ['pacientes'],
        synonyms: [
            'status tratamento',
            'status do tratamento',
            'situacao tratamento',
            'tratamento status',
        ],
    },
    consulta_data: {
        label: 'Data da consulta',
        kinds: ['agenda'],
        required: true,
        synonyms: ['data', 'dia', 'data da consulta', 'consulta data', 'date'],
    },
    consulta_hora: {
        label: 'Hora',
        kinds: ['agenda'],
        required: true,
        synonyms: ['hora', 'horario', 'horário', 'hora da consulta', 'time'],
    },
    consulta_tipo: {
        label: 'Tipo',
        kinds: ['agenda'],
        synonyms: ['tipo', 'tipo de consulta', 'procedimento', 'servico', 'serviço', 'motivo'],
    },
    consulta_profissional: {
        label: 'Profissional',
        kinds: ['agenda'],
        synonyms: [
            'profissional',
            'medico',
            'médico',
            'dentista',
            'doutor',
            'dr',
            'dra',
            'responsavel',
        ],
    },
    consulta_status: {
        label: 'Status da consulta',
        kinds: ['agenda'],
        synonyms: ['status', 'situacao', 'situação', 'compareceu', 'presenca', 'presença'],
    },
    pagamento_descricao: {
        label: 'Descrição',
        kinds: ['financeiro'],
        synonyms: [
            'descricao',
            'descrição',
            'historico',
            'histórico',
            'parcela',
            'lancamento',
            'lançamento',
            'item',
        ],
    },
    pagamento_valor: {
        label: 'Valor',
        kinds: ['financeiro'],
        required: true,
        synonyms: ['valor', 'valor pago', 'total', 'quantia', 'amount'],
    },
    pagamento_vencimento: {
        label: 'Vencimento',
        kinds: ['financeiro'],
        required: true,
        synonyms: ['vencimento', 'data', 'data de vencimento', 'vence em', 'due'],
    },
    pagamento_pago_em: {
        label: 'Pago em',
        kinds: ['financeiro'],
        synonyms: ['pago em', 'data do pagamento', 'pagamento', 'pago', 'paid'],
    },
    pagamento_forma: {
        label: 'Forma',
        kinds: ['financeiro'],
        synonyms: ['forma', 'forma de pagamento', 'meio', 'metodo', 'método', 'pagamento forma'],
    },
};
// Planilha completa: cada linha pode trazer paciente, tratamento, consulta e lançamento.
for (const info of Object.values(CLINIC_IMPORT_FIELD_INFO))
    info.kinds.push('completo');
/** Obrigatório para o tipo: na planilha completa, só o nome. */
export function importFieldRequired(field, kind) {
    const info = CLINIC_IMPORT_FIELD_INFO[field];
    if (kind === 'completo')
        return field === 'nome';
    return Boolean(info.required) && info.kinds.includes(kind);
}
/** Coluna da planilha escolhida para cada campo (null = não importar). */
export const clinicImportMappingSchema = z.partialRecord(clinicImportFieldSchema, z.string().nullable());
export const CLINIC_IMPORT_STATUSES = ['rascunho', 'feita', 'desfeita'];
export const clinicImportStatusSchema = z.enum(CLINIC_IMPORT_STATUSES);
export const clinicImportCountsSchema = z.object({
    total: z.number().int().min(0),
    ok: z.number().int().min(0),
    quarantine: z.number().int().min(0),
    created: z.number().int().min(0),
    updated: z.number().int().min(0),
    // Planilha completa: o que cada linha vai virar (relatório de conferência antes de gravar).
    patients: z.number().int().min(0).optional(),
    treatments: z.number().int().min(0).optional(),
    appointments: z.number().int().min(0).optional(),
    payments: z.number().int().min(0).optional(),
    /** Soma dos valores dos lançamentos, para bater com a planilha. */
    paymentsCents: z.number().int().optional(),
    /** Tratamentos que não estão no catálogo e vão entrar nele. */
    newTreatments: z.array(z.string()).optional(),
    /** Origens que não casaram com a lista da clínica (entram como vieram). */
    unknownOrigins: z.array(z.string()).optional(),
    /** Pacientes que já existem na clínica e só ganham o que faltava. */
    existing: z.number().int().min(0).optional(),
});
export const clinicImportPreviewRowSchema = z.object({
    line: z.number().int().min(1),
    /** Valores já normalizados por campo (o que vai entrar). */
    values: z.record(z.string(), z.string()),
    /** Vazio quando a linha passa. */
    problems: z.array(z.string()),
});
export const clinicImportSchema = z.object({
    id: z.uuid(),
    kind: clinicImportKindSchema,
    source: clinicImportSourceSchema,
    fileName: z.string().nullable(),
    status: clinicImportStatusSchema,
    columns: z.array(z.string()),
    mapping: clinicImportMappingSchema,
    counts: clinicImportCountsSchema,
    preview: z.array(clinicImportPreviewRowSchema),
    createdAt: z.iso.datetime(),
    undoneAt: z.iso.datetime().nullable(),
    /** Até quando dá para desfazer (null depois do prazo ou quando já foi desfeita). */
    canUndoUntil: z.iso.datetime().nullable(),
});
export const clinicImportRowSchema = z.object({
    id: z.uuid(),
    line: z.number().int().min(1),
    /** Valores crus da planilha, por coluna. */
    raw: z.record(z.string(), z.string()),
    reason: z.string(),
    resolvedAt: z.iso.datetime().nullable(),
});
export const updateClinicImportSchema = z.object({
    kind: clinicImportKindSchema.optional(),
    mapping: clinicImportMappingSchema.optional(),
});
/** Corrigir uma linha da quarentena: os valores por campo, já no formato da prévia. */
export const retryClinicImportRowSchema = z.object({
    values: z.partialRecord(clinicImportFieldSchema, z.string()),
});
/** Modelos para baixar: cabeçalhos por tipo, na ordem em que aparecem. */
export const CLINIC_IMPORT_TEMPLATES = {
    pacientes: [
        'nome',
        'telefone',
        'cpf',
        'email',
        'nascimento',
        'sexo',
        'origem',
        'status',
        'tratamento',
        'tratamento_inicio',
        'tratamento_meses',
        'tratamento_preco',
        'tratamento_status',
        'observacoes',
    ],
    agenda: [
        'nome',
        'telefone',
        'consulta_data',
        'consulta_hora',
        'consulta_tipo',
        'consulta_profissional',
        'consulta_status',
    ],
    financeiro: [
        'nome',
        'telefone',
        'pagamento_descricao',
        'pagamento_valor',
        'pagamento_vencimento',
        'pagamento_pago_em',
        'pagamento_forma',
    ],
    // Uma linha por acontecimento: o mesmo paciente pode aparecer em várias linhas.
    completo: [
        'nome',
        'telefone',
        'cpf',
        'email',
        'nascimento',
        'sexo',
        'origem',
        'status',
        'tratamento',
        'tratamento_inicio',
        'tratamento_meses',
        'tratamento_preco',
        'tratamento_status',
        'consulta_data',
        'consulta_hora',
        'consulta_tipo',
        'consulta_status',
        'pagamento_descricao',
        'pagamento_valor',
        'pagamento_vencimento',
        'pagamento_pago_em',
        'pagamento_forma',
        'observacoes',
    ],
};
